import { KpiCard, AreaChart, BarChartV, DonutRing, RankBars, DashCard, ActivityFeed, Gauge } from '../components/DashboardWidgets';
import React, { useState, useEffect, useCallback } from 'react';
import { PageHeader, StatCards, Card, TabBar, Field, inputStyle, btnPrimary, btnSm, Empty, fmtKES , useBreakpoint } from '../components/UI';
import client, { getFeedback } from '../api/client';
import { getBrandingSlug, getBrandingName } from '../utils/formatters';

const CAMPAIGN_TYPES = [
  { id: 'social_media', label: 'Social Media', icon: '📱' },
  { id: 'email',        label: 'Email Campaign', icon: '📧' },
  { id: 'sms',          label: 'SMS Blast', icon: '💬' },
  { id: 'print',        label: 'Print / Outdoor', icon: '🖨️' },
  { id: 'influencer',   label: 'Influencer / PR', icon: '🌟' },
  { id: 'promo',        label: 'Promotions / Offers', icon: '🎁' },
  { id: 'event',        label: 'Events & Expos', icon: '🎪' },
  { id: 'partnership',  label: 'Partnerships / B2B', icon: '🤝' },
];

const PROMO_TYPES = [
  { id: 'discount',     label: 'Room Discount %' },
  { id: 'free_night',   label: 'Free Night' },
  { id: 'package',      label: 'Package Deal' },
  { id: 'early_bird',   label: 'Early Bird Rate' },
  { id: 'flash_sale',   label: 'Flash Sale' },
  { id: 'loyalty',      label: 'Loyalty Reward' },
];

const STATUS_COLOR = {
  draft: '#888', active: '#2ecc71', paused: '#e67e22', completed: '#3498db', cancelled: '#e74c3c'
};

const blank = { title: '', type: 'social_media', channel: '', target_audience: '', budget: '', start_date: '', end_date: '', description: '', status: 'draft', goal: '', kpi: '', assigned_to: '' };

export default function MarketingPage({ notify }) {
  const bp = useBreakpoint();
  const [tab, setTab] = useState('dashboard');
  const [campaigns, setCampaigns] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [promos, setPromos] = useState([]);

  // Campaign form
  const [showCampForm, setShowCampForm] = useState(false);
  const [campForm, setCampForm] = useState(blank);
  const [editCamp, setEditCamp] = useState(null);

  // Promo form
  const [showPromoForm, setShowPromoForm] = useState(false);
  const [promoForm, setPromoForm] = useState({ title: '', type: 'discount', discount_percent: '', promo_code: '', valid_from: '', valid_until: '', conditions: '', is_active: true });

  // Content calendar
  const [contentPosts, setContentPosts] = useState([
    { id: 1, platform: 'Instagram', date: '', caption: '', status: 'draft', topic: '' },
  ]);

  const load = useCallback(async () => {
    try {
      const r = await getFeedback();
      setReviews((r.data.results || r.data).slice(0, 50));
    } catch {}
    // Load campaigns and promos from backend (#70/#71)
    try {
      const r = await client.get('/marketing/campaigns/');
      setCampaigns(r.data || []);
    } catch {
      // Fallback to localStorage if backend endpoint not yet available
      try {
        const saved = JSON.parse(localStorage.getItem('gm_campaigns') || '[]');
        setCampaigns(saved);
      } catch {}
    }
    try {
      const r = await client.get('/marketing/promos/');
      setPromos(r.data || []);
    } catch {
      try {
        const savedPromos = JSON.parse(localStorage.getItem('gm_promos') || '[]');
        setPromos(savedPromos);
      } catch {}
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveCampaign = async () => {
    if (!campForm.title) { notify('Campaign title required', 'error'); return; }
    try {
      if (editCamp) {
        await client.patch(`/marketing/campaigns/${editCamp.id}/`, campForm);
      } else {
        await client.post('/marketing/campaigns/', { ...campForm, id: Date.now(), created_at: new Date().toISOString() });
      }
      await load();
    } catch {
      // Fallback to localStorage
      const newCamp = editCamp
        ? campaigns.map(c => c.id === editCamp.id ? { ...c, ...campForm } : c)
        : [...campaigns, { ...campForm, id: Date.now(), created_at: new Date().toISOString() }];
      setCampaigns(newCamp);
      localStorage.setItem('gm_campaigns', JSON.stringify(newCamp));
    }
    setShowCampForm(false);
    setCampForm(blank);
    setEditCamp(null);
    notify(editCamp ? 'Campaign updated' : 'Campaign created');
  };

  const deleteCampaign = async (id) => {
    try {
      await client.delete(`/marketing/campaigns/${id}/`);
      await load();
    } catch {
      const updated = campaigns.filter(c => c.id !== id);
      setCampaigns(updated);
      localStorage.setItem('gm_campaigns', JSON.stringify(updated));
    }
    notify('Campaign deleted');
  };

  const updateCampStatus = async (id, status) => {
    try {
      await client.patch(`/marketing/campaigns/${id}/`, { status });
      await load();
    } catch {
      const updated = campaigns.map(c => c.id === id ? { ...c, status } : c);
      setCampaigns(updated);
      localStorage.setItem('gm_campaigns', JSON.stringify(updated));
    }
  };

  const savePromo = async () => {
    if (!promoForm.title || !promoForm.promo_code) { notify('Title and promo code required', 'error'); return; }
    try {
      await client.post('/marketing/promos/', { ...promoForm, id: Date.now(), created_at: new Date().toISOString() });
      await load();
    } catch {
      const newPromos = [...promos, { ...promoForm, id: Date.now(), created_at: new Date().toISOString() }];
      setPromos(newPromos);
      localStorage.setItem('gm_promos', JSON.stringify(newPromos));
    }
    setShowPromoForm(false);
    setPromoForm({ title: '', type: 'discount', discount_percent: '', promo_code: '', valid_from: '', valid_until: '', conditions: '', is_active: true });
    notify('Promotion created!');
  };

  const active = campaigns.filter(c => c.status === 'active').length;
  const totalBudget = campaigns.reduce((s, c) => s + parseFloat(c.budget || 0), 0);
  const avgRating = reviews.length ? (reviews.reduce((s, r) => s + (r.rating || 5), 0) / reviews.length).toFixed(1) : '—';

  return (
    <div>
      <PageHeader icon="📣" title="Marketing & Promotions" sub="Campaigns, promotions, content calendar & reviews" />

      <StatCards stats={[
        { label:'Active Campaigns', value:active,            color:'#2ecc71', icon:'📣', sub:`${campaigns.length} total` },
        { label:'Total Budget',     value:fmtKES(totalBudget),color:'#c8a96e', icon:'💰' },
        { label:'Avg Review Rating',value:`${avgRating} ★`,  color:'#e67e22', icon:'⭐', sub:`${reviews.length} reviews` },
        { label:'Total Campaigns',  value:campaigns.length,  color:'#3498db', icon:'📊' },
      ]} />

      <TabBar tabs={[
        { id: 'dashboard',  label: 'Dashboard',       icon: '📊' },
        { id: 'campaigns',  label: 'Campaigns',       icon: '📣' },
        { id: 'promotions', label: 'Promotions',      icon: '🎁' },
        { id: 'reviews',    label: 'Guest Reviews',   icon: '⭐' },
        { id: 'content',    label: 'Content Calendar',icon: '📅' },
        { id: 'analytics',  label: 'Analytics',       icon: '📈' },
        { id: 'sms',        label: 'Bulk SMS',          icon: '📱' },
        { id: 'email',      label: 'Bulk Email',        icon: '📧' },
        { id: 'ai_studio',  label: 'AI Content Studio', icon: '🤖' },
      ]} active={tab} onChange={setTab} />

      {/* ── DASHBOARD ── */}
      {tab === 'dashboard' && (
        <div style={{ display:'grid', gap:14 }}>

          {/* Row 1: KPI strip */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:10 }}>
            {[
              { label:'Active Campaigns', value:campaigns.filter(c=>c.status==='active').length, color:'#2ecc71', icon:'📣', sub:`${campaigns.length} total` },
              { label:'Total Budget',     value:fmtKES(totalBudget), color:'#c8a96e', icon:'💰' },
              { label:'Active Promos',    value:promos.filter(p=>p.is_active).length, color:'#3498db', icon:'🎁', sub:`${promos.length} total` },
              { label:'Avg Rating',       value:avgRating+' ★', color:'#e67e22', icon:'⭐', sub:`${reviews.length} reviews` },
            ].map((s,i)=>(
              <div key={i} style={{ background:'var(--bgCard)', border:'1px solid var(--border)', borderTop:`3px solid ${s.color}`, borderRadius:12, padding:'16px 18px 12px', position:'relative', overflow:'hidden' }}
                onMouseEnter={e=>e.currentTarget.style.boxShadow=`0 6px 24px ${s.color}20`}
                onMouseLeave={e=>e.currentTarget.style.boxShadow='none'}>
                <div style={{ position:'absolute', top:-20, right:-12, fontSize:36, opacity:0.07 }}>{s.icon}</div>
                <div style={{ fontSize:10, color:'var(--textFaint)', fontWeight:700, textTransform:'uppercase', letterSpacing:1.5, marginBottom:6 }}>{s.label}</div>
                <div style={{ fontSize:24, fontWeight:800, color:'var(--textPrimary)', letterSpacing:-0.5 }}>{s.value}</div>
                {s.sub && <div style={{ fontSize:10, color:'var(--textFaint)', marginTop:4 }}>{s.sub}</div>}
              </div>
            ))}
          </div>

          {/* Row 2: Active campaigns + Reviews + Promos */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 200px', gap:12 }}>
            <DashCard title="Active Campaigns">
              {campaigns.filter(c=>c.status==='active').length === 0
                ? <Empty text="No active campaigns" />
                : campaigns.filter(c=>c.status==='active').slice(0,6).map(cam=>(
                  <div key={cam.id} style={{ display:'flex', justifyContent:'space-between', padding:'9px 0', borderBottom:'1px solid var(--border)' }}>
                    <div>
                      <div style={{ fontSize:13, color:'var(--textPrimary)', fontWeight:600 }}>{cam.title}</div>
                      <div style={{ fontSize:11, color:'var(--textFaint)' }}>{CAMPAIGN_TYPES.find(t=>t.id===cam.type)?.icon} {cam.channel}</div>
                    </div>
                    <span style={{ color:'#c8a96e', fontWeight:700, fontSize:12 }}>{cam.budget ? fmtKES(cam.budget) : '—'}</span>
                  </div>
                ))
              }
            </DashCard>

            <DashCard title="Recent Guest Reviews">
              {reviews.length === 0
                ? <Empty text="No reviews yet" />
                : reviews.slice(0,5).map((r,i)=>(
                  <div key={i} style={{ padding:'8px 0', borderBottom:'1px solid var(--border)' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:2 }}>
                      <span style={{ fontSize:13, color:'var(--textPrimary)', fontWeight:600 }}>{r.name||'Anonymous'}</span>
                      <span style={{ color:'#c8a96e', fontSize:13 }}>{'★'.repeat(r.rating||5)}</span>
                    </div>
                    <div style={{ fontSize:11, color:'var(--textMuted)', lineHeight:1.5 }}>{(r.message||'').slice(0,90)}{(r.message||'').length>90?'…':''}</div>
                  </div>
                ))
              }
            </DashCard>

            <DashCard title="Campaign Mix">
              <DonutRing
                size={100} stroke={11}
                centerLabel={campaigns.length}
                centerSub="total"
                segments={[
                  { label:'Active',   value:campaigns.filter(c=>c.status==='active').length,   color:'#2ecc71' },
                  { label:'Paused',   value:campaigns.filter(c=>c.status==='paused').length,   color:'#e67e22' },
                  { label:'Draft',    value:campaigns.filter(c=>c.status==='draft').length,    color:'#3498db' },
                  { label:'Ended',    value:campaigns.filter(c=>c.status==='ended').length,    color:'var(--textFaint)' },
                ].filter(s=>s.value>0)}
              />
              <div style={{ marginTop:12, display:'flex', flexDirection:'column', gap:6 }}>
                {[
                  ['📣 New Campaign', ()=>{ setTab('campaigns'); setShowCampForm(true); }],
                  ['🎁 New Promo',    ()=>{ setTab('promotions'); setShowPromoForm(true); }],
                  ['📈 Analytics',   ()=>setTab('analytics')],
                ].map(([l,fn])=>(
                  <button key={l} onClick={fn} style={{ background:'var(--bgInput)', border:'1px solid var(--border)', borderRadius:6, padding:'8px 10px', color:'var(--primary)', fontSize:11, cursor:'pointer', fontFamily:'inherit', textAlign:'left' }}>{l}</button>
                ))}
              </div>
            </DashCard>
          </div>
        </div>
      )}

      {/* ── CAMPAIGNS ── */}
      {tab === 'campaigns' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <button style={btnPrimary} onClick={() => { setShowCampForm(true); setEditCamp(null); setCampForm(blank); }}>+ New Campaign</button>
          </div>

          {showCampForm && (
            <Card title={editCamp ? 'Edit Campaign' : 'New Campaign'}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 12, marginBottom: 14 }}>
                <div style={{ gridColumn: '1/-1' }}>
                  <Field label="Campaign Title *"><input style={inputStyle} value={campForm.title} onChange={e => setCampForm({ ...campForm, title: e.target.value })} placeholder="e.g. Valentine's Weekend Getaway" /></Field>
                </div>
                <Field label="Campaign Type">
                  <select style={inputStyle} value={campForm.type} onChange={e => setCampForm({ ...campForm, type: e.target.value })}>
                    {CAMPAIGN_TYPES.map(t => <option key={t.id} value={t.id}>{t.icon} {t.label}</option>)}
                  </select>
                </Field>
                <Field label="Channel / Platform"><input style={inputStyle} value={campForm.channel} onChange={e => setCampForm({ ...campForm, channel: e.target.value })} placeholder="e.g. Instagram, WhatsApp, Billboards" /></Field>
                <Field label="Target Audience"><input style={inputStyle} value={campForm.target_audience} onChange={e => setCampForm({ ...campForm, target_audience: e.target.value })} placeholder="e.g. Couples, Corporates, Families" /></Field>
                <Field label="Budget (KES)"><input style={inputStyle} type="number" value={campForm.budget} onChange={e => setCampForm({ ...campForm, budget: e.target.value })} placeholder="0" /></Field>
                <Field label="Start Date"><input style={inputStyle} type="date" value={campForm.start_date} onChange={e => setCampForm({ ...campForm, start_date: e.target.value })} /></Field>
                <Field label="End Date"><input style={inputStyle} type="date" value={campForm.end_date} onChange={e => setCampForm({ ...campForm, end_date: e.target.value })} /></Field>
                <Field label="Assigned To"><input style={inputStyle} value={campForm.assigned_to} onChange={e => setCampForm({ ...campForm, assigned_to: e.target.value })} placeholder="Team member name" /></Field>
                <Field label="Goal / Objective">
                  <input style={inputStyle} value={campForm.goal} onChange={e => setCampForm({ ...campForm, goal: e.target.value })} placeholder="e.g. 50 bookings, 1000 followers" />
                </Field>
                <Field label="KPI / Success Metric">
                  <input style={inputStyle} value={campForm.kpi} onChange={e => setCampForm({ ...campForm, kpi: e.target.value })} placeholder="e.g. CTR, bookings, impressions" />
                </Field>
                <div style={{ gridColumn: '1/-1' }}>
                  <Field label="Description / Brief">
                    <textarea style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }} value={campForm.description} onChange={e => setCampForm({ ...campForm, description: e.target.value })} placeholder="Campaign details, messaging strategy, notes…" />
                  </Field>
                </div>
                <Field label="Status">
                  <select style={inputStyle} value={campForm.status} onChange={e => setCampForm({ ...campForm, status: e.target.value })}>
                    {Object.keys(STATUS_COLOR).map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                  </select>
                </Field>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={btnPrimary} onClick={saveCampaign}>✓ Save Campaign</button>
                <button style={{ ...btnSm, padding: '10px 18px' }} onClick={() => { setShowCampForm(false); setEditCamp(null); }}>Cancel</button>
              </div>
            </Card>
          )}

          <Card title={`All Campaigns (${campaigns.length})`}>
            {campaigns.length === 0 ? <Empty text="No campaigns yet. Create your first one above." /> : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead><tr style={{ borderBottom: '1px solid #2a2a2a' }}>
                    {['Campaign', 'Type', 'Channel', 'Budget', 'Dates', 'Status', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '8px 10px', color: '#c8a96e', textAlign: 'left', fontSize: 11, textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {campaigns.map(c => (
                      <tr key={c.id} style={{ borderBottom: '1px solid #1a1a1a' }}>
                        <td style={{ padding: '9px 10px' }}>
                          <div style={{ color:'var(--textPrimary)', fontWeight: 600 }}>{c.title}</div>
                          {c.target_audience && <div style={{ fontSize: 11, color:'var(--textFaint)' }}>{c.target_audience}</div>}
                        </td>
                        <td style={{ padding: '9px 10px', color:'var(--textMuted)', fontSize: 11 }}>{CAMPAIGN_TYPES.find(t => t.id === c.type)?.icon} {CAMPAIGN_TYPES.find(t => t.id === c.type)?.label}</td>
                        <td style={{ padding: '9px 10px', color:'var(--textMuted)', fontSize: 12 }}>{c.channel || '—'}</td>
                        <td style={{ padding: '9px 10px', color: '#c8a96e', fontWeight: 700 }}>{c.budget ? fmtKES(c.budget) : '—'}</td>
                        <td style={{ padding: '9px 10px', color:'var(--textFaint)', fontSize: 11 }}>{c.start_date || '—'} {c.end_date ? `→ ${c.end_date}` : ''}</td>
                        <td style={{ padding: '9px 10px' }}>
                          <span style={{ padding: '2px 8px', borderRadius: 3, fontSize: 10, fontWeight: 700, color: STATUS_COLOR[c.status], background: STATUS_COLOR[c.status] + '22', textTransform: 'uppercase' }}>{c.status}</span>
                        </td>
                        <td style={{ padding: '9px 10px' }}>
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {c.status !== 'active' && <button style={{ ...btnSm, fontSize: 10 }} onClick={() => updateCampStatus(c.id, 'active')}>Activate</button>}
                            {c.status === 'active' && <button style={{ ...btnSm, fontSize: 10 }} onClick={() => updateCampStatus(c.id, 'paused')}>Pause</button>}
                            <button style={{ ...btnSm, fontSize: 10 }} onClick={() => { setEditCamp(c); setCampForm(c); setShowCampForm(true); }}>Edit</button>
                            <button style={{ ...btnSm, fontSize: 10, color: '#e74c3c' }} onClick={() => deleteCampaign(c.id)}>Del</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      {/* ── PROMOTIONS ── */}
      {tab === 'promotions' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <button style={btnPrimary} onClick={() => setShowPromoForm(true)}>+ New Promotion</button>
          </div>

          {showPromoForm && (
            <Card title="Create Promotion">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 12, marginBottom: 14 }}>
                <div style={{ gridColumn: '1/-1' }}>
                  <Field label="Promotion Title *"><input style={inputStyle} value={promoForm.title} onChange={e => setPromoForm({ ...promoForm, title: e.target.value })} placeholder="e.g. 20% Off Weekend Stays" /></Field>
                </div>
                <Field label="Promo Type">
                  <select style={inputStyle} value={promoForm.type} onChange={e => setPromoForm({ ...promoForm, type: e.target.value })}>
                    {PROMO_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                </Field>
                <Field label="Promo Code *"><input style={inputStyle} value={promoForm.promo_code} onChange={e => setPromoForm({ ...promoForm, promo_code: e.target.value.toUpperCase() })} placeholder="SAVE20" /></Field>
                <Field label="Discount %"><input style={inputStyle} type="number" min="0" max="100" value={promoForm.discount_percent} onChange={e => setPromoForm({ ...promoForm, discount_percent: e.target.value })} placeholder="0" /></Field>
                <Field label="Valid From"><input style={inputStyle} type="date" value={promoForm.valid_from} onChange={e => setPromoForm({ ...promoForm, valid_from: e.target.value })} /></Field>
                <Field label="Valid Until"><input style={inputStyle} type="date" value={promoForm.valid_until} onChange={e => setPromoForm({ ...promoForm, valid_until: e.target.value })} /></Field>
                <div style={{ gridColumn: '1/-1' }}>
                  <Field label="Terms & Conditions"><textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} value={promoForm.conditions} onChange={e => setPromoForm({ ...promoForm, conditions: e.target.value })} placeholder="E.g. Min 2 nights, not combinable with other offers…" /></Field>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={btnPrimary} onClick={savePromo}>✓ Create Promotion</button>
                <button style={{ ...btnSm, padding: '10px 18px' }} onClick={() => setShowPromoForm(false)}>Cancel</button>
              </div>
            </Card>
          )}

          <Card title={`Promotions (${promos.length})`}>
            {promos.length === 0 ? <Empty text="No promotions yet" /> : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 12 }}>
                {promos.map(p => (
                  <div key={p.id} style={{ background:'var(--bgCard)', border: `1px solid ${p.is_active ? '#c8a96e44' : 'var(--borderMid)'}`, borderRadius: 4, padding: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color:'var(--textPrimary)' }}>{p.title}</div>
                      <span style={{ padding: '2px 7px', borderRadius: 3, fontSize: 10, background: p.is_active ? '#2ecc7122' : 'var(--bgInput)', color: p.is_active ? '#2ecc71' : '#555' }}>{p.is_active ? 'ACTIVE' : 'INACTIVE'}</span>
                    </div>
                    <div style={{ fontFamily: 'monospace', fontSize: 18, color: '#c8a96e', fontWeight: 700, marginBottom: 4 }}>{p.promo_code}</div>
                    {p.discount_percent && <div style={{ color: '#2ecc71', fontSize: 13, marginBottom: 4 }}>{p.discount_percent}% discount</div>}
                    {p.valid_until && <div style={{ color:'var(--textFaint)', fontSize: 11 }}>Expires: {p.valid_until}</div>}
                    {p.conditions && <div style={{ color:'var(--textFaint)', fontSize: 11, marginTop: 6 }}>{p.conditions}</div>}
                    <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                      <button style={{ ...btnSm, fontSize: 10 }} onClick={() => {
                        const updated = promos.map(x => x.id === p.id ? { ...x, is_active: !x.is_active } : x);
                        setPromos(updated);
                        localStorage.setItem('gm_promos', JSON.stringify(updated));
                      }}>{p.is_active ? 'Deactivate' : 'Activate'}</button>
                      <button style={{ ...btnSm, fontSize: 10, color: '#e74c3c' }} onClick={() => {
                        const updated = promos.filter(x => x.id !== p.id);
                        setPromos(updated);
                        localStorage.setItem('gm_promos', JSON.stringify(updated));
                      }}>Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </>
      )}

      {/* ── REVIEWS ── */}
      {tab === 'reviews' && (
        <Card title={`Guest Reviews (${reviews.length})`}>
          {reviews.length === 0 ? <Empty text="No guest reviews yet" /> : (
            <>
              {/* Rating summary */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(120px,1fr))', gap: 10, marginBottom: 20 }}>
                {[1,2,3,4,5].map(star => {
                  const count = reviews.filter(r => r.rating === star).length;
                  const pct = reviews.length ? Math.round(count / reviews.length * 100) : 0;
                  return (
                    <div key={star} style={{ textAlign: 'center', padding: '12px 8px', background:'var(--bgCard)', borderRadius: 4, border: '1px solid #2a2a2a' }}>
                      <div style={{ color: '#c8a96e', fontSize: 18 }}>{'★'.repeat(star)}</div>
                      <div style={{ color:'var(--textPrimary)', fontWeight: 700, fontSize: 20 }}>{count}</div>
                      <div style={{ color:'var(--textFaint)', fontSize: 11 }}>{pct}%</div>
                    </div>
                  );
                })}
              </div>

              {/* Category filter */}
              <div style={{ marginBottom: 16, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {['All', ...new Set(reviews.map(r => r.category).filter(Boolean))].map(cat => (
                  <button key={cat} style={{ background: 'var(--bgInput)', border: '1px solid #2a2a2a', borderRadius: 3, padding: '4px 12px', color: '#c8a96e', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}>{cat}</button>
                ))}
              </div>

              {reviews.map((r, i) => (
                <div key={i} style={{ padding: '12px 0', borderBottom: '1px solid #1a1a1a' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <div>
                      <span style={{ color:'var(--textPrimary)', fontWeight: 600, fontSize: 14 }}>{r.name || 'Anonymous Guest'}</span>
                      {r.category && <span style={{ marginLeft: 8, fontSize: 10, padding: '2px 7px', background: 'var(--bgInput)', borderRadius: 10, color: '#c8a96e', border: '1px solid #c8a96e44' }}>{r.category}</span>}
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{ color: '#c8a96e' }}>{'★'.repeat(r.rating || 5)}{'☆'.repeat(5 - (r.rating || 5))}</span>
                      <span style={{ color:'var(--textFaint)', fontSize: 11 }}>{r.created_at?.slice(0, 10) || ''}</span>
                    </div>
                  </div>
                  <p style={{ color:'var(--textMuted)', fontSize: 13, lineHeight: 1.7, margin: 0 }}>{r.message}</p>
                  {r.email && <div style={{ color:'var(--textFaint)', fontSize: 11, marginTop: 4 }}>{r.email}</div>}
                </div>
              ))}
            </>
          )}
        </Card>
      )}

      {/* ── CONTENT CALENDAR ── */}
      {tab === 'content' && (
        <Card title="Content Planning Calendar">
          <p style={{ color:'var(--textMuted)', fontSize: 13, marginBottom: 16 }}>Plan your social media posts, blog articles, email newsletters, and other content.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 12, marginBottom: 16 }}>
            {['Instagram', 'Facebook', 'TikTok', 'Twitter/X', 'Email Newsletter', 'Blog / Website', 'WhatsApp', 'LinkedIn'].map(platform => (
              <div key={platform} style={{ background:'var(--bgCard)', border: '1px solid #2a2a2a', borderRadius: 4, padding: 14 }}>
                <div style={{ fontWeight: 700, color:'var(--textPrimary)', fontSize: 13, marginBottom: 8 }}>{platform}</div>
                <div style={{ fontSize: 11, color:'var(--textFaint)', marginBottom: 8 }}>Content ideas for {platform}:</div>
                <textarea
                  style={{ ...inputStyle, minHeight: 80, resize: 'vertical', fontSize: 12 }}
                  placeholder={`Draft content ideas for ${platform}…\nE.g. Morning pool shots, weekend packages, event photos…`}
                />
              </div>
            ))}
          </div>
          <div style={{ padding: '14px', background: '#1a1400', border: '1px solid #c8a96e44', borderRadius: 4, fontSize: 13, color: '#c8a96e' }}>
            💡 Content Tip: Consistency is key. Aim for 3–5 posts per week on Instagram and Facebook. Use high-quality photos of your rooms, pool, food, and events. Always include your contact and booking link.
          </div>
        </Card>
      )}

      {/* ── ANALYTICS ── */}
      {tab === 'analytics' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 16 }}>
          <Card title="Campaign Performance Tracker">
            <p style={{ color:'var(--textMuted)', fontSize: 13, marginBottom: 14 }}>Log your campaign performance metrics here.</p>
            {campaigns.map(c => (
              <div key={c.id} style={{ padding: '10px 0', borderBottom: '1px solid #1a1a1a' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ color:'var(--textPrimary)', fontWeight: 600, fontSize: 13 }}>{c.title}</span>
                  <span style={{ color: STATUS_COLOR[c.status], fontSize: 11, textTransform: 'uppercase' }}>{c.status}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 8, marginTop: 6 }}>
                  {['Impressions', 'Clicks', 'Conversions'].map(metric => (
                    <div key={metric} style={{ background:'var(--bgCard)', borderRadius: 3, padding: '6px 8px', textAlign: 'center' }}>
                      <div style={{ fontSize: 9, color:'var(--textFaint)', textTransform: 'uppercase', letterSpacing: 1 }}>{metric}</div>
                      <input type="number" placeholder="0" style={{ ...inputStyle, textAlign: 'center', padding: '3px 6px', fontSize: 14, background: 'transparent', border: 'none', borderBottom: '1px solid #2a2a2a' }} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {campaigns.length === 0 && <Empty text="Create campaigns to track performance" />}
          </Card>

          <Card title="Review Analytics">
            <div style={{ padding: '16px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
                <div style={{ fontSize: 56, fontWeight: 700, color: '#c8a96e', fontFamily: "'Cormorant Garamond',serif" }}>{avgRating}</div>
                <div>
                  <div style={{ fontSize: 24, color: '#c8a96e' }}>{'★'.repeat(Math.round(parseFloat(avgRating) || 0))}</div>
                  <div style={{ color:'var(--textMuted)', fontSize: 13 }}>Average Rating</div>
                  <div style={{ color:'var(--textFaint)', fontSize: 11 }}>{reviews.length} total reviews</div>
                </div>
              </div>
              {[5, 4, 3, 2, 1].map(star => {
                const count = reviews.filter(r => r.rating === star).length;
                const pct = reviews.length ? count / reviews.length * 100 : 0;
                return (
                  <div key={star} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <span style={{ color: '#c8a96e', width: 20, fontSize: 12 }}>{star}★</span>
                    <div style={{ flex: 1, height: 8, background: 'var(--bgInput)', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: '#c8a96e', borderRadius: 4 }} />
                    </div>
                    <span style={{ color:'var(--textFaint)', width: 24, fontSize: 11, textAlign: 'right' }}>{count}</span>
                  </div>
                );
              })}

              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: 11, color: '#c8a96e', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 10 }}>Reviews by Category</div>
                {[...new Set(reviews.map(r => r.category).filter(Boolean))].map(cat => {
                  const catReviews = reviews.filter(r => r.category === cat);
                  const catAvg = (catReviews.reduce((s, r) => s + (r.rating || 5), 0) / catReviews.length).toFixed(1);
                  return (
                    <div key={cat} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #1a1a1a', fontSize: 13 }}>
                      <span style={{ color:'var(--textMuted)' }}>{cat}</span>
                      <span style={{ color: '#c8a96e', fontWeight: 600 }}>{catAvg} ★ ({catReviews.length})</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>
        </div>
      )}
      {tab === 'sms'       && <SMSCampaignPanel notify={notify} />}
      {tab === 'email'     && <EmailCampaignPanel notify={notify} />}
      {tab === 'ai_studio' && <AIContentStudio notify={notify} />}
    </div>
  );
}


// ─── SMS Campaign Panel ────────────────────────────────────────────────────────
// Uses Africa's Talking API routed through your Django backend
// Backend endpoint: POST /api/marketing/send-sms/
function SMSCampaignPanel({ notify }) {
  const [message, setMessage]       = useState('');
  const [segment, setSegment]       = useState('all_guests');
  const [customNums, setCustomNums] = useState('');
  const [scheduled, setScheduled]   = useState('');
  const [sending, setSending]       = useState(false);
  const [history, setHistory]       = useState(() => {
    try { return JSON.parse(localStorage.getItem('gm_sms_history') || '[]'); } catch { return []; }
  });
  const [preview, setPreview]       = useState(false);

  const SEGMENTS = [
    { id: 'all_guests',    label: 'All Guests (ever)',     desc: 'Everyone in the guest database' },
    { id: 'current',       label: 'Currently Checked-In',  desc: 'Guests in-house right now' },
    { id: 'past_30',       label: 'Last 30 Days',          desc: 'Guests who checked out in last 30 days' },
    { id: 'past_90',       label: 'Last 90 Days',          desc: 'Guests who checked out in last 90 days' },
    { id: 'custom',        label: 'Custom Numbers',         desc: 'Paste phone numbers manually' },
  ];

  const charCount = message.length;
  const smsCount  = Math.ceil(charCount / 160) || 1;
  const isOver    = charCount > 160;

  const TEMPLATES = [
    { label: '🎉 Weekend Offer', text: 'Grand Memories Resort & Spa: Weekend special! Book Fri-Sun & get 20% off + free breakfast. Call 0700 000 000 or reply YES to book. T&Cs apply.' },
    { label: '🎄 Holiday Package', text: 'Grand Memories Resort & Spa: Celebrate the festive season with us! Special holiday packages from KES 12,000/night. Call 0700 000 000. Limited rooms.' },
    { label: '🍽️ Restaurant Promo', text: 'Grand Memories Resort & Spa Restaurant: Enjoy our Friday Nyama Choma Special - KES 1,200 all-inclusive. Reservations: 0700 000 000.' },
    { label: '💼 Conference Offer', text: 'Grand Memories Resort & Spa: Book our conference facilities this month & get 15% off. Capacity up to 200 pax. Enquire: 0700 000 000.' },
    { label: '⭐ Loyalty Reward', text: 'Dear Valued Guest, as a thank-you for your loyalty, enjoy 15% off your next stay. Use code LOYAL15 at booking. Grand Memories Resort & Spa.' },
  ];

  const send = async () => {
    if (!message.trim()) { notify('Please enter a message', 'error'); return; }
    if (charCount > 480) { notify('Message too long (max 3 SMS / 480 chars)', 'error'); return; }
    if (segment === 'custom' && !customNums.trim()) { notify('Enter at least one phone number', 'error'); return; }
    setSending(true);
    try {
      const payload = { message, segment, custom_numbers: customNums, scheduled_at: scheduled };
      // In production this hits your Django backend which calls Africa's Talking
      // await api.sendBulkSMS(payload);
      
      // Demo mode — log to history
      const record = {
        id: Date.now(), message, segment, chars: charCount,
        sent_at: new Date().toISOString(), status: 'sent',
        recipients_est: segment === 'custom' ? customNums.split('\n').filter(Boolean).length : '~Auto',
      };
      const newHistory = [record, ...history].slice(0, 50);
      setHistory(newHistory);
      localStorage.setItem('gm_sms_history', JSON.stringify(newHistory));
      notify(`✅ SMS campaign queued! Africa's Talking will send to ${record.recipients_est} recipients.`);
      setMessage(''); setCustomNums(''); setPreview(false);
    } catch(e) { notify(e.response?.data?.error || 'Send failed — check backend config', 'error'); }
    finally { setSending(false); }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 16 }}>
      {/* Compose */}
      <div>
        <Card title="📱 Compose SMS Campaign">
          {/* Provider info */}
          <div style={{ display:'flex',alignItems:'center',gap:10,padding:'10px 14px',
            background:'var(--bgSuccess)',borderRadius:6,border:'1px solid #2ecc7133',marginBottom:16 }}>
            <span style={{ fontSize:20 }}>🌍</span>
            <div>
              <div style={{ fontWeight:700,color:'#2ecc71',fontSize:12 }}>Powered by Africa's Talking</div>
              <div style={{ color:'var(--textMuted)',fontSize:11 }}>Sender ID: GrandMemories · ~KES 1.20/SMS · Coverage: Kenya + 18 African countries</div>
            </div>
            <a href="https://africastalking.com" target="_blank" rel="noreferrer"
              style={{ marginLeft:'auto',fontSize:10,color:'#3498db' }}>Dashboard ↗</a>
          </div>

          {/* Templates */}
          <div style={{ marginBottom:14 }}>
            <div style={{ fontSize:11,color:'var(--textMuted)',marginBottom:6,fontWeight:600 }}>QUICK TEMPLATES</div>
            <div style={{ display:'flex',flexWrap:'wrap',gap:6 }}>
              {TEMPLATES.map((t,i) => (
                <button key={i} style={{ fontSize:11,padding:'4px 10px',borderRadius:4,
                  background:'var(--bgInput)',color:'var(--textSecond)',border:'1px solid var(--border)',
                  cursor:'pointer' }} onClick={() => setMessage(t.text)}>{t.label}</button>
              ))}
            </div>
          </div>

          {/* Message composer */}
          <Field label="Message *">
            <textarea
              style={{ ...inputStyle, minHeight:100, fontFamily:'monospace', fontSize:13,
                border:`1px solid ${isOver?'#e74c3c':'var(--border)'}` }}
              placeholder="Type your SMS message here... Keep it concise and include a clear call-to-action."
              value={message} onChange={e => setMessage(e.target.value)}
            />
          </Field>
          <div style={{ display:'flex',justifyContent:'space-between',marginTop:4,marginBottom:14 }}>
            <span style={{ fontSize:11,color:isOver?'#e74c3c':'var(--textMuted)' }}>
              {charCount}/160 chars {smsCount > 1 && `· ${smsCount} SMS segments`}
            </span>
            {charCount > 0 && (
              <span style={{ fontSize:11,color:'#c8a96e' }}>
                Est. cost: KES {(smsCount * 1.2).toFixed(2)} × recipients
              </span>
            )}
          </div>

          {/* Audience segment */}
          <div style={{ marginBottom:14 }}>
            <div style={{ fontSize:11,color:'var(--textMuted)',marginBottom:8,fontWeight:600 }}>TARGET AUDIENCE</div>
            <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))',gap:8 }}>
              {SEGMENTS.map(s => (
                <div key={s.id} onClick={() => setSegment(s.id)}
                  style={{ padding:'10px 12px',borderRadius:6,cursor:'pointer',
                    background: segment===s.id?'#1a3a1a':'var(--bgInput)',
                    border:`1px solid ${segment===s.id?'#2ecc71':'var(--border)'}` }}>
                  <div style={{ fontWeight:600,color:segment===s.id?'#2ecc71':'var(--textPrimary)',fontSize:12 }}>{s.label}</div>
                  <div style={{ fontSize:10,color:'var(--textMuted)',marginTop:2 }}>{s.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Custom numbers */}
          {segment === 'custom' && (
            <Field label="Phone Numbers (one per line, E.164 format: +254712345678)">
              <textarea style={{ ...inputStyle, minHeight:80, fontFamily:'monospace', fontSize:12 }}
                placeholder={'+254712345678\n+254798765432\n+254733000000'}
                value={customNums} onChange={e => setCustomNums(e.target.value)} />
              <div style={{ fontSize:10,color:'var(--textMuted)',marginTop:3 }}>
                {customNums.split('\n').filter(l=>l.trim()).length} numbers entered
              </div>
            </Field>
          )}

          {/* Schedule (optional) */}
          <Field label="Schedule Send (optional — leave blank to send immediately)">
            <input type="datetime-local" style={inputStyle} value={scheduled}
              onChange={e => setScheduled(e.target.value)} />
          </Field>

          <div style={{ display:'flex',gap:8,marginTop:16 }}>
            <button style={btnPrimary} onClick={() => setPreview(true)} disabled={!message.trim()}>
              👁 Preview Campaign
            </button>
          </div>
        </Card>

        {/* Preview modal */}
        {preview && (
          <Card title="Campaign Preview" style={{ marginTop:16 }}>
            <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))',gap:12,marginBottom:16 }}>
              <div style={{ background:'var(--bgInput)',padding:12,borderRadius:6 }}>
                <div style={{ fontSize:10,color:'var(--textMuted)' }}>Audience</div>
                <div style={{ fontWeight:700,color:'var(--textPrimary)' }}>{SEGMENTS.find(s=>s.id===segment)?.label}</div>
              </div>
              <div style={{ background:'var(--bgInput)',padding:12,borderRadius:6 }}>
                <div style={{ fontSize:10,color:'var(--textMuted)' }}>Message Length</div>
                <div style={{ fontWeight:700,color:isOver?'#e74c3c':'#2ecc71' }}>{charCount} chars · {smsCount} SMS</div>
              </div>
            </div>
            {/* Phone mockup */}
            <div style={{ display:'flex',justifyContent:'center',marginBottom:16 }}>
              <div style={{ width:260,background:'var(--bgCard)',borderRadius:20,padding:'20px 16px',
                border:'2px solid #333',boxShadow:'0 8px 32px #00000088' }}>
                <div style={{ fontSize:10,color:'var(--textFaint)',textAlign:'center',marginBottom:12 }}>GrandMemories</div>
                <div style={{ background:'#1e1e1e',borderRadius:12,padding:'10px 12px',
                  fontSize:12,color:'var(--textPrimary)',lineHeight:1.5,wordBreak:'break-word' }}>{message}</div>
                <div style={{ fontSize:9,color:'var(--textFaint)',textAlign:'right',marginTop:6 }}>Now</div>
              </div>
            </div>
            <div style={{ display:'flex',gap:8 }}>
              <button style={{ ...btnPrimary, background:'linear-gradient(135deg,#27ae60,#1e8449)', flex:1 }}
                onClick={send} disabled={sending}>
                {sending ? '⏳ Sending via Africa\'s Talking…' : `🚀 Send SMS Campaign`}
              </button>
              <button style={{ ...btnSm, background:'var(--bgInput)', color:'var(--textMuted)' }} onClick={() => setPreview(false)}>
                ✏️ Edit
              </button>
            </div>
          </Card>
        )}
      </div>

      {/* History sidebar */}
      <div>
        <Card title={`📋 Sent Campaigns (${history.length})`}>
          {history.length === 0 ? (
            <div style={{ textAlign:'center',padding:20,color:'var(--textMuted)',fontSize:12 }}>No campaigns sent yet</div>
          ) : history.map(h => (
            <div key={h.id} style={{ padding:'10px 12px',borderBottom:'1px solid var(--border)',
              background:'var(--bgInput)',borderRadius:4,marginBottom:6 }}>
              <div style={{ fontSize:12,color:'var(--textSecond)',lineHeight:1.4,marginBottom:4 }}>
                {h.message.length > 60 ? h.message.slice(0,60)+'…' : h.message}
              </div>
              <div style={{ display:'flex',justifyContent:'space-between',fontSize:10,color:'var(--textMuted)' }}>
                <span>{SEGMENTS.find(s=>s.id===h.segment)?.label || h.segment}</span>
                <span>{new Date(h.sent_at).toLocaleDateString('en-KE')}</span>
              </div>
              <div style={{ fontSize:10,color:'#2ecc71',marginTop:2 }}>
                ✓ {h.recipients_est} recipients · {h.chars} chars
              </div>
            </div>
          ))}
        </Card>

        {/* Setup guide */}
        <Card title="⚙️ Africa's Talking Setup" style={{ marginTop:12 }}>
          <div style={{ fontSize:11,color:'var(--textSecond)',lineHeight:1.7 }}>
            <div style={{ fontWeight:700,color:'var(--textPrimary)',marginBottom:8 }}>To activate live SMS:</div>
            {[
              '1. Sign up at africastalking.com',
              '2. Create a live app & get your API key',
              '3. Register Sender ID: "GrandMemories"',
              '4. Top up wallet via M-Pesa',
              '5. Add AT_USERNAME and AT_API_KEY to backend .env',
              '6. pip install africastalking in backend',
            ].map((s,i) => (
              <div key={i} style={{ padding:'4px 0',color: i<2?'var(--textPrimary)':'var(--textMuted)' }}>{s}</div>
            ))}
            <a href="https://developers.africastalking.com/tutorials" target="_blank" rel="noreferrer"
              style={{ display:'block',marginTop:8,color:'#3498db',fontSize:11 }}>View Developer Docs ↗</a>
          </div>
        </Card>
      </div>
    </div>
  );
}


// ─── Email Campaign Panel ──────────────────────────────────────────────────────
// Uses Brevo (formerly Sendinblue) via Django backend
// Backend endpoint: POST /api/marketing/send-email/
function EmailCampaignPanel({ notify }) {
  const [subject, setSubject]         = useState('');
  const [previewText, setPreviewText] = useState('');
  const [body, setBody]               = useState('');
  const [segment, setSegment]         = useState('all_guests');
  const [fromName, setFromName]       = useState('Grand Memories Resort & Spa');
  const [sending, setSending]         = useState(false);
  const [activeTab, setActiveTab]     = useState('compose'); // compose | preview | history
  const [history, setHistory]         = useState(() => {
    try { return JSON.parse(localStorage.getItem('gm_email_history') || '[]'); } catch { return []; }
  });

  const SEGMENTS = [
    { id: 'all_guests',  label: 'All Guests' },
    { id: 'current',     label: 'Currently Checked-In' },
    { id: 'past_30',     label: 'Last 30 Days' },
    { id: 'past_90',     label: 'Last 90 Days' },
    { id: 'vip',         label: 'VIP / Repeat Guests' },
  ];

  const TEMPLATES = [
    {
      label: '🎉 Weekend Getaway',
      subject: 'Escape to Grand Memories Resort & Spa This Weekend 🌿',
      preview: 'Exclusive weekend rates — book by Thursday',
      body: `<h2>Your Weekend Escape Awaits</h2>
<p>Dear Valued Guest,</p>
<p>Unwind at Grand Memories Resort & Spa this weekend with our exclusive <strong>Weekend Getaway Package</strong>:</p>
<ul>
  <li>✅ 20% off room rates (Fri–Sun)</li>
  <li>✅ Complimentary breakfast for two</li>
  <li>✅ Free access to our swimming pool & spa</li>
  <li>✅ Late checkout until 1 PM</li>
</ul>
<p>📞 Call us on <strong>0700 000 000</strong> or reply to this email to book your stay.</p>
<p><em>Offer valid for bookings made before Thursday. Subject to availability.</em></p>
<p>Warm regards,<br/><strong>Grand Memories Resort & Spa</strong></p>`,
    },
    {
      label: '💼 Conference Package',
      subject: 'Host Your Next Event at Grand Memories Resort & Spa',
      preview: 'World-class conference facilities in the heart of Nairobi',
      body: `<h2>Your Event, Our Excellence</h2>
<p>Dear Business Partner,</p>
<p>Grand Memories Resort & Spa offers <strong>premium conference and event facilities</strong> for every occasion:</p>
<ul>
  <li>🏛️ Savanna Hall — Up to 200 guests</li>
  <li>🪑 Acacia Boardroom — Executive meetings, 20 pax</li>
  <li>📽️ Serengeti Suite — 80-person conferences</li>
  <li>🌄 Kilimanjaro Room — Panoramic views, 50 pax</li>
</ul>
<p>All venues include high-speed WiFi, AV equipment, and our dedicated events team.</p>
<p>📧 Email events@grandmemories.co.ke or call <strong>0700 000 000</strong> for a customised quote.</p>`,
    },
    {
      label: '⭐ Loyalty Offer',
      subject: 'A Special Thank-You from Grand Memories Resort & Spa',
      preview: 'Exclusive loyalty reward inside',
      body: `<h2>Thank You for Your Loyalty</h2>
<p>Dear Valued Guest,</p>
<p>As a <strong>returning guest</strong>, you deserve something special. We are delighted to offer you an exclusive loyalty reward:</p>
<p style="font-size:24px;color:#c8a96e;text-align:center"><strong>15% OFF</strong> your next stay</p>
<p style="text-align:center">Use code: <strong>LOYAL15</strong> when booking</p>
<p>This offer is valid for the next 60 days and applies to all room types. We look forward to welcoming you back.</p>
<p>Warm regards,<br/><strong>Grand Memories Resort & Spa</strong></p>`,
    },
  ];

  const applyTemplate = (t) => {
    setSubject(t.subject);
    setPreviewText(t.preview);
    setBody(t.body);
  };

  const send = async () => {
    if (!subject.trim() || !body.trim()) { notify('Subject and body are required', 'error'); return; }
    setSending(true);
    try {
      // In production: await api.sendBulkEmail({ subject, preview_text: previewText, html_content: body, segment, from_name: fromName });
      const record = {
        id: Date.now(), subject, segment, from_name: fromName,
        sent_at: new Date().toISOString(), status: 'sent',
        recipients_est: segment === 'all_guests' ? 'All Guests' : SEGMENTS.find(s=>s.id===segment)?.label,
      };
      const newHistory = [record, ...history].slice(0, 50);
      setHistory(newHistory);
      localStorage.setItem('gm_email_history', JSON.stringify(newHistory));
      notify('✅ Email campaign queued via Brevo! Recipients will receive it shortly.');
      setSubject(''); setPreviewText(''); setBody('');
    } catch(e) { notify('Send failed — check Brevo API config', 'error'); }
    finally { setSending(false); }
  };

  return (
    <div>
      {/* Provider banner */}
      <div style={{ display:'flex',alignItems:'center',gap:10,padding:'10px 16px',
        background:'var(--bgHover)',borderRadius:6,border:'1px solid #3498db33',marginBottom:16 }}>
        <span style={{ fontSize:22 }}>📧</span>
        <div>
          <div style={{ fontWeight:700,color:'#3498db',fontSize:12 }}>Powered by Brevo (formerly Sendinblue)</div>
          <div style={{ color:'var(--textMuted)',fontSize:11 }}>Free tier: 300 emails/day · Unlimited contacts · Full tracking & open rates</div>
        </div>
        <a href="https://app.brevo.com" target="_blank" rel="noreferrer"
          style={{ marginLeft:'auto',fontSize:10,color:'#3498db' }}>Brevo Dashboard ↗</a>
      </div>

      {/* Sub-tabs */}
      <div style={{ display:'flex',gap:8,marginBottom:16 }}>
        {['compose','preview','history'].map(t => (
          <button key={t} onClick={() => setActiveTab(t)} style={{
            padding:'6px 16px',borderRadius:4,border:'none',cursor:'pointer',fontSize:12,fontWeight:600,
            background: activeTab===t?'#c8a96e':'var(--bgInput)',
            color: activeTab===t?'#111':'var(--textMuted)',
          }}>{t.charAt(0).toUpperCase()+t.slice(1)}</button>
        ))}
      </div>

      {activeTab === 'compose' && (
        <div style={{ display:'grid',gridTemplateColumns:'1fr 300px',gap:16 }}>
          <Card title="Compose Email">
            {/* Templates */}
            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:11,color:'var(--textMuted)',marginBottom:6,fontWeight:600 }}>QUICK TEMPLATES</div>
              <div style={{ display:'flex',gap:8,flexWrap:'wrap' }}>
                {TEMPLATES.map((t,i) => (
                  <button key={i} onClick={() => applyTemplate(t)} style={{ fontSize:11,padding:'5px 12px',
                    borderRadius:4,background:'var(--bgInput)',color:'var(--textSecond)',
                    border:'1px solid var(--border)',cursor:'pointer' }}>{t.label}</button>
                ))}
              </div>
            </div>
            <Field label="From Name">
              <input style={inputStyle} value={fromName} onChange={e=>setFromName(e.target.value)} />
            </Field>
            <Field label="Subject Line *">
              <input style={inputStyle} placeholder="e.g. Exclusive Weekend Offer at Grand Memories Resort & Spa 🌿"
                value={subject} onChange={e=>setSubject(e.target.value)} />
              <div style={{ fontSize:10,color: subject.length>60?'#e74c3c':'var(--textMuted)',marginTop:3 }}>
                {subject.length}/60 chars (ideal for inbox display)
              </div>
            </Field>
            <Field label="Preview Text (shows in inbox before opening)">
              <input style={inputStyle} placeholder="One-line teaser shown in Gmail/Outlook preview..."
                value={previewText} onChange={e=>setPreviewText(e.target.value)} />
            </Field>
            <Field label="Email Body (HTML supported) *">
              <textarea style={{ ...inputStyle, minHeight:200, fontFamily:'monospace', fontSize:12 }}
                placeholder="Write your email body here. HTML tags like <h2>, <p>, <strong>, <ul> are supported."
                value={body} onChange={e=>setBody(e.target.value)} />
            </Field>
            <Field label="Target Audience">
              <select style={inputStyle} value={segment} onChange={e=>setSegment(e.target.value)}>
                {SEGMENTS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </Field>
            <div style={{ display:'flex',gap:8,marginTop:12 }}>
              <button style={btnPrimary} onClick={() => setActiveTab('preview')}>👁 Preview Email</button>
            </div>
          </Card>

          {/* Tips sidebar */}
          <div>
            <Card title="✉️ Email Tips">
              {[
                { tip:'Subject line', detail:'Keep under 50 chars. Use emojis sparingly. Avoid "FREE" in caps.' },
                { tip:'Personalise', detail:'Use {{guest_name}} — Brevo supports merge tags automatically.' },
                { tip:'CTA button', detail:'Add a clear call-to-action: "Book Now", "Claim Offer", "Call Us".' },
                { tip:'Best time', detail:'Send Tue–Thu, 9–11am or 7–9pm for highest open rates.' },
                { tip:'Unsubscribe', detail:'Brevo auto-adds an unsubscribe link — required by email law.' },
              ].map((item,i) => (
                <div key={i} style={{ padding:'8px 0',borderBottom:'1px solid var(--border)' }}>
                  <div style={{ fontWeight:700,color:'#c8a96e',fontSize:11 }}>{item.tip}</div>
                  <div style={{ fontSize:11,color:'var(--textMuted)',marginTop:2 }}>{item.detail}</div>
                </div>
              ))}
            </Card>
            <Card title="⚙️ Brevo Setup" style={{ marginTop:12 }}>
              <div style={{ fontSize:11,color:'var(--textSecond)',lineHeight:1.7 }}>
                {['1. Sign up free at brevo.com','2. Get your API key from Settings','3. Verify your sender email domain','4. Add BREVO_API_KEY to backend .env','5. pip install brevo-python'].map((s,i) => (
                  <div key={i} style={{ padding:'3px 0' }}>{s}</div>
                ))}
                <a href="https://developers.brevo.com" target="_blank" rel="noreferrer"
                  style={{ display:'block',marginTop:6,color:'#3498db',fontSize:11 }}>API Docs ↗</a>
              </div>
            </Card>
          </div>
        </div>
      )}

      {activeTab === 'preview' && (
        <Card title="Email Preview">
          <div style={{ marginBottom:12,padding:'10px 14px',background:'var(--bgInput)',borderRadius:6,
            display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:10 }}>
            <div><div style={{ fontSize:10,color:'var(--textMuted)' }}>From</div><div style={{ fontWeight:600,color:'var(--textPrimary)',fontSize:12 }}>{fromName}</div></div>
            <div><div style={{ fontSize:10,color:'var(--textMuted)' }}>Subject</div><div style={{ fontWeight:600,color:'var(--textPrimary)',fontSize:12 }}>{subject||'(no subject)'}</div></div>
            <div><div style={{ fontSize:10,color:'var(--textMuted)' }}>Audience</div><div style={{ fontWeight:600,color:'#c8a96e',fontSize:12 }}>{SEGMENTS.find(s=>s.id===segment)?.label}</div></div>
          </div>
          {previewText && <div style={{ fontSize:11,color:'var(--textMuted)',marginBottom:12,fontStyle:'italic' }}>Preview: {previewText}</div>}
          {/* Email rendered preview */}
          <div style={{ background:'#fff',borderRadius:8,padding:'24px 32px',color:'#111',
            border:'1px solid #ddd',maxWidth:600,margin:'0 auto',
            fontFamily:'Arial,sans-serif',fontSize:14,lineHeight:1.6 }}
            dangerouslySetInnerHTML={{ __html: body || '<p style="color:#999;text-align:center">Compose an email to see the preview</p>' }}
          />
          <div style={{ display:'flex',gap:8,marginTop:16,justifyContent:'center' }}>
            <button style={{ ...btnPrimary, background:'linear-gradient(135deg,#2980b9,#1a5276)' }}
              onClick={send} disabled={sending || !subject.trim() || !body.trim()}>
              {sending ? '⏳ Sending via Brevo…' : '🚀 Send Email Campaign'}
            </button>
            <button style={{ ...btnSm, background:'var(--bgInput)',color:'var(--textMuted)' }} onClick={() => setActiveTab('compose')}>✏️ Edit</button>
          </div>
        </Card>
      )}

      {activeTab === 'history' && (
        <Card title={`Sent Email Campaigns (${history.length})`}>
          {history.length === 0 ? <div style={{ textAlign:'center',padding:30,color:'var(--textMuted)' }}>No campaigns sent yet</div> :
            <table style={{ width:'100%',borderCollapse:'collapse',fontSize:12 }}>
              <thead><tr style={{ background:'var(--bgInput)' }}>
                {['Date','Subject','Audience','Status'].map(h => (
                  <th key={h} style={{ padding:'8px 12px',textAlign:'left',color:'var(--textMuted)',fontSize:10,fontWeight:700,borderBottom:'1px solid var(--border)' }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {history.map(h => (
                  <tr key={h.id} style={{ borderBottom:'1px solid var(--border)' }}>
                    <td style={{ padding:'8px 12px',color:'var(--textMuted)',fontSize:11 }}>{new Date(h.sent_at).toLocaleDateString('en-KE')}</td>
                    <td style={{ padding:'8px 12px',fontWeight:600,color:'var(--textPrimary)' }}>{h.subject}</td>
                    <td style={{ padding:'8px 12px',color:'var(--textSecond)' }}>{h.recipients_est}</td>
                    <td style={{ padding:'8px 12px' }}><span style={{ color:'#2ecc71',fontWeight:700,fontSize:11 }}>✓ Sent</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          }
        </Card>
      )}
    </div>
  );
}


// ─── AI Content Studio ────────────────────────────────────────────────────────
// Powered by Anthropic Claude API (claude-sonnet-4-20250514)
function AIContentStudio({ notify }) {
  const bp = useBreakpoint();
  const [contentType, setContentType] = useState('email');
  const [prompt, setPrompt]           = useState('');
  const [result, setResult]           = useState('');
  const [loading, setLoading]         = useState(false);
  const [copied, setCopied]           = useState(false);
  const [history, setHistory]         = useState([]);

  const CONTENT_TYPES = [
    { id: 'email',      icon: '📧', label: 'Email Campaign',        hint: 'Describe the offer, target guest, and season' },
    { id: 'sms',        icon: '📱', label: 'SMS Campaign',          hint: 'Describe the promotion (will be kept under 160 chars)' },
    { id: 'blog',       icon: '✍️', label: 'Blog Post Outline',     hint: 'Give a topic + target keyword (e.g. "weekend getaway Nairobi")' },
    { id: 'meta',       icon: '🔍', label: 'SEO Meta Tags',         hint: 'Describe the webpage (e.g. "our swimming pool page")' },
    { id: 'promo',      icon: '🎉', label: 'Promotion Copy',        hint: 'Describe the deal, dates, and target audience' },
    { id: 'review',     icon: '⭐', label: 'Review Response',       hint: 'Paste the guest review you want to respond to' },
    { id: 'social',     icon: '📸', label: 'Social Media Post',     hint: 'Describe the photo/moment + target platform (Instagram, X, Facebook)' },
    { id: 'seo_audit',  icon: '🔎', label: 'SEO Page Audit',        hint: 'Paste your webpage text for an SEO analysis and improvement tips' },
  ];

  const SYSTEM_PROMPTS = {
    email: `You are a luxury hospitality marketing expert for Grand Memories Resort & Spa — a premium hotel in Nairobi, Kenya. 
Write compelling email marketing campaigns. Always include:
1. Subject line (under 50 chars, with emoji)
2. Preview text (under 100 chars)
3. Email body with HTML formatting (h2, p, ul, strong tags)
4. Clear call-to-action
Keep tone: warm, aspirational, professional. Reference Kenyan hospitality and nature.`,

    sms: `You are a hospitality SMS marketing expert for Grand Memories Resort & Spa, Nairobi, Kenya.
Write a bulk SMS message. STRICT RULES:
- Maximum 160 characters (count every character including spaces)
- Start with "Grand Memories Resort & Spa:" 
- Include a clear offer and CTA
- End with "T&Cs apply" or phone number if space allows
After the SMS, show the exact character count.`,

    blog: `You are an SEO content strategist for Grand Memories Resort & Spa, a luxury hotel in Nairobi, Kenya.
Create a detailed, SEO-optimised blog post outline. Include:
- H1 title with primary keyword
- Target keyword and 3 secondary keywords
- Meta description (155 chars)
- 5-7 H2 sections with bullet points for each
- FAQ section (3 questions)
- Call-to-action conclusion
Focus on Kenya travel, Nairobi, Rift Valley, East African tourism.`,

    meta: `You are an SEO expert for Grand Memories Resort & Spa, a luxury hotel in Nairobi, Kenya.
Generate optimised meta tags. Return EXACTLY this format:
TITLE (50-60 chars): [title here]
DESCRIPTION (150-160 chars): [description here]  
PRIMARY KEYWORD: [keyword]
SECONDARY KEYWORDS: [3-5 keywords, comma separated]
TIPS: [2-3 specific improvement tips for this page]`,

    promo: `You are a luxury hotel marketing copywriter for Grand Memories Resort & Spa, Nairobi, Kenya.
Write engaging promotional copy in multiple formats:
1. Email subject line
2. SMS version (160 chars)
3. Social media caption (Instagram)
4. Short web banner text
Keep tone aspirational, warm, and urgent. Use Kenyan context where relevant.`,

    review: `You are the Guest Relations Manager at Grand Memories Resort & Spa, a luxury hotel in Nairobi, Kenya.
Write a professional, warm, personalised response to a guest review.
Rules:
- Thank the guest by name if mentioned
- Address any specific concerns raised
- Highlight a positive from their review
- Invite them back warmly
- Keep under 100 words
- Sign off as "The Grand Memories Resort & Spa Team"`,

    social: `You are a social media manager for Grand Memories Resort & Spa, Nairobi, Kenya.
Write an engaging social media post. Include:
1. Caption with line breaks (mobile-friendly)
2. 10-15 relevant hashtags
3. Suggested posting time
4. Emoji usage for the platform tone
Make it aspirational and shareable. Reference Kenyan wildlife, nature, luxury if relevant.`,

    seo_audit: `You are an SEO consultant auditing content for Grand Memories Resort & Spa, Nairobi Kenya.
Analyse the provided text and return:
1. CURRENT ISSUES: List 3-5 specific SEO problems
2. TARGET KEYWORDS: Suggest 5 primary keywords this page should rank for
3. REWRITTEN VERSION: Rewrite the text with better keyword integration, headings, and structure
4. QUICK WINS: 3 immediate actions to improve ranking
5. ESTIMATED IMPACT: Which keywords have best ranking potential and why`,
  };

  const EXAMPLE_PROMPTS = {
    email:     'Write a Valentine\'s Day package email for couples — 2 nights, candlelit dinner, couples spa treatment, special rate KES 28,000',
    sms:       'Weekend flash sale: 25% off all rooms this Fri-Sun only, limited to first 10 bookings',
    blog:      'Write an outline for a blog post targeting the keyword "luxury hotels Nairobi" for couples planning a romantic weekend',
    meta:      'Our conference facilities page — featuring 4 venues, capacity up to 200, WiFi, AV equipment, catering',
    promo:     'Easter holiday family package: 3 nights, pool access, daily breakfast, kids eat free, 15% off for bookings before March 31',
    review:    '"The pool was lovely but our room had a noisy air conditioner that kept us up at night. Breakfast was excellent though. Staff very friendly." — 3 stars',
    social:    'Sunrise photo from our rooftop terrace showing the Nairobi skyline — posting on Instagram',
    seo_audit: 'Welcome to Grand Memories Resort & Spa Hotel. We offer rooms and services. Located in Nairobi. Book with us for a good stay. We have a pool and restaurant.',
  };

  const generate = async () => {
    if (!prompt.trim()) { notify('Please describe what you want to create', 'error'); return; }
    setLoading(true);
    setResult('');
    try {
      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 1500,
          system: SYSTEM_PROMPTS[contentType],
          messages: [{ role: 'user', content: prompt }],
        }),
      });
      const data = await response.json();
      if (data.error) throw new Error(data.error.message);
      const text = (data.content || []).map(c => c.text || '').join('');
      setResult(text);
      setHistory(prev => [{ type: contentType, prompt: prompt.slice(0,60), result: text, at: new Date().toISOString() }, ...prev].slice(0,10));
    } catch(e) {
      if (e.message?.includes('401') || e.message?.includes('403')) {
        notify('API key not configured — this is a demo. In production, requests route through your Django backend.', 'error');
        setResult(`[DEMO MODE — API key required in production]\n\nIn your Django backend, add:\n\nANTHROPIC_API_KEY=sk-ant-your-key-here\n\nThen route /api/marketing/ai-generate/ through Claude.\n\nExample output for "${prompt}":\n\n[AI would generate ${contentType} content here based on your prompt]`);
      } else {
        notify('AI generation failed: ' + e.message, 'error');
      }
    }
    finally { setLoading(false); }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(result).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    });
  };

  const currentType = CONTENT_TYPES.find(t => t.id === contentType);

  return (
    <div>
      {/* Header banner */}
      <div style={{ display:'flex',alignItems:'center',gap:12,padding:'12px 16px',
        background:'linear-gradient(135deg,#1a0a2a,#0a1a2a)',borderRadius:8,
        border:'1px solid #9b59b633',marginBottom:16 }}>
        <span style={{ fontSize:28 }}>🤖</span>
        <div>
          <div style={{ fontWeight:700,color:'#c8a96e',fontSize:14 }}>AI Content Studio · Powered by Claude</div>
          <div style={{ color:'var(--textMuted)',fontSize:11 }}>
            Generate SEO-optimised hotel marketing copy, blog outlines, social posts, SMS campaigns & more — in seconds.
          </div>
        </div>
      </div>

      <div style={{ display:'grid',gridTemplateColumns: bp === 'sm' ? '1fr' : '1fr 380px',gap:16 }}>
        {/* Left: Generator */}
        <div>
          {/* Content type selector */}
          <Card title="Choose Content Type">
            <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:8,marginBottom:4 }}>
              {CONTENT_TYPES.map(t => (
                <div key={t.id} onClick={() => { setContentType(t.id); setResult(''); }}
                  style={{ padding:'10px 8px',borderRadius:6,cursor:'pointer',textAlign:'center',
                    background: contentType===t.id?'#2a1a3a':'var(--bgInput)',
                    border:`1px solid ${contentType===t.id?'#9b59b6':'var(--border)'}`,
                    transition:'all 0.15s' }}>
                  <div style={{ fontSize:20,marginBottom:4 }}>{t.icon}</div>
                  <div style={{ fontSize:10,fontWeight:700,color:contentType===t.id?'#c8a96e':'var(--textSecond)' }}>{t.label}</div>
                </div>
              ))}
            </div>
          </Card>

          {/* Prompt */}
          <Card title={`✏️ Describe Your ${currentType?.label}`} style={{ marginTop:12 }}>
            <div style={{ fontSize:11,color:'var(--textMuted)',marginBottom:8,padding:'6px 10px',
              background:'var(--bgInput)',borderRadius:4 }}>
              💡 <strong style={{ color:'var(--textSecond)' }}>Hint:</strong> {currentType?.hint}
            </div>
            <button style={{ fontSize:11,padding:'4px 10px',marginBottom:8,borderRadius:4,
              background:'var(--bgInput)',color:'#3498db',border:'1px solid #3498db33',cursor:'pointer' }}
              onClick={() => setPrompt(EXAMPLE_PROMPTS[contentType] || '')}>
              Try example prompt
            </button>
            <textarea
              style={{ ...inputStyle, minHeight:100, fontSize:13, width:'100%', boxSizing:'border-box' }}
              placeholder={`Describe what you need... e.g. ${EXAMPLE_PROMPTS[contentType]}`}
              value={prompt} onChange={e => setPrompt(e.target.value)}
              onKeyDown={e => { if (e.ctrlKey && e.key==='Enter') generate(); }}
            />
            <div style={{ fontSize:10,color:'var(--textMuted)',marginTop:3 }}>Tip: Press Ctrl+Enter to generate</div>
            <button style={{ ...btnPrimary, marginTop:10, width:'100%',
              background:'linear-gradient(135deg,#6c3483,#1a237e)',fontSize:14,padding:'12px' }}
              onClick={generate} disabled={loading || !prompt.trim()}>
              {loading ? '🤖 Claude is writing…' : `✨ Generate ${currentType?.label}`}
            </button>
          </Card>

          {/* Result */}
          {(result || loading) && (
            <Card title={`📄 Generated ${currentType?.label}`} style={{ marginTop:12 }}>
              {loading ? (
                <div style={{ textAlign:'center',padding:30 }}>
                  <div style={{ fontSize:24,marginBottom:8 }}>🤖</div>
                  <div style={{ color:'var(--textMuted)',fontSize:12 }}>Claude is crafting your content…</div>
                </div>
              ) : (
                <>
                  <div style={{ whiteSpace:'pre-wrap',fontFamily:'monospace',fontSize:12,
                    color:'var(--textSecond)',background:'var(--bgInput)',padding:14,
                    borderRadius:6,lineHeight:1.7,maxHeight:400,overflowY:'auto',
                    border:'1px solid var(--border)' }}>
                    {result}
                  </div>
                  <div style={{ display:'flex',gap:8,marginTop:10,flexWrap:'wrap' }}>
                    <button style={{ ...btnSm,background:copied?'#1a3a1a':'#1a2a3a',
                      color:copied?'#2ecc71':'#3498db' }} onClick={copyToClipboard}>
                      {copied ? '✓ Copied!' : '📋 Copy to Clipboard'}
                    </button>
                    <button style={{ ...btnSm,background:'var(--bgInput)',color:'var(--textMuted)' }}
                      onClick={() => { setResult(''); setPrompt(''); }}>
                      🔄 Generate New
                    </button>
                    <button style={{ ...btnSm,background:'var(--bgHover)',color:'#9b59b6' }}
                      onClick={() => { setPrompt(prompt + ' (make it shorter)'); generate(); }}>
                      ✂️ Make Shorter
                    </button>
                    <button style={{ ...btnSm,background:'var(--bgHover)',color:'#c8a96e' }}
                      onClick={() => { setPrompt(prompt + ' (make it more compelling and urgent)'); generate(); }}>
                      🔥 More Compelling
                    </button>
                  </div>
                </>
              )}
            </Card>
          )}
        </div>

        {/* Right: SEO tips + history */}
        <div>
          <Card title="🔍 Hotel SEO Quick Guide">
            <div style={{ fontSize:11,lineHeight:1.7 }}>
              {[
                { label:'Top Keywords to Target', color:'#c8a96e', items:[
                  '"hotel near Nairobi CBD"','"luxury hotels Nairobi"',
                  '"conference rooms Nairobi hire"','"weekend getaway Kenya"',
                  '"swimming pool hotel Nairobi"','"Nairobi airport hotel"',
                ]},
                { label:'Content That Ranks', color:'#2ecc71', items:[
                  'Blog: "Top 10 things to do in Nairobi"','Blog: "Best wedding venues Nairobi"',
                  'Blog: "Corporate retreat ideas Kenya"','FAQ pages for each room type',
                  'Local area guide (restaurants, attractions)',
                ]},
                { label:'Technical SEO Checklist', color:'#3498db', items:[
                  '✓ Google Business Profile (keep updated)','✓ Schema markup for hotel (JSON-LD)',
                  '✓ Mobile-first, fast page load','✓ Secure HTTPS throughout',
                  '✓ Submit sitemap to Google Search Console',
                ]},
              ].map((section,i) => (
                <div key={i} style={{ marginBottom:12 }}>
                  <div style={{ fontWeight:700,color:section.color,fontSize:10,marginBottom:4,textTransform:'uppercase' }}>{section.label}</div>
                  {section.items.map((item,j) => (
                    <div key={j} style={{ color:'var(--textMuted)',padding:'1px 0' }}>{item}</div>
                  ))}
                </div>
              ))}
            </div>
          </Card>

          <Card title="🛠️ Recommended SEO Tools" style={{ marginTop:12 }}>
            {[
              { name:'Semrush', desc:'Keyword research + competitor analysis', url:'semrush.com', color:'#e67e22', price:'From $130/mo' },
              { name:'Surfer SEO', desc:'Optimize pages against top Google results', url:'surferseo.com', color:'#3498db', price:'From $99/mo' },
              { name:'Google Search Console', desc:'Track rankings & fix issues — FREE', url:'search.google.com/search-console', color:'#2ecc71', price:'Free' },
              { name:'Google Business Profile', desc:'Local SEO — essential for hotels — FREE', url:'business.google.com', color:'#2ecc71', price:'Free' },
            ].map((tool,i) => (
              <div key={i} style={{ padding:'8px 0',borderBottom:'1px solid var(--border)' }}>
                <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center' }}>
                  <div style={{ fontWeight:700,color:tool.color,fontSize:12 }}>{tool.name}</div>
                  <span style={{ fontSize:10,color:tool.price==='Free'?'#2ecc71':'var(--textMuted)',fontWeight:600 }}>{tool.price}</span>
                </div>
                <div style={{ fontSize:11,color:'var(--textMuted)' }}>{tool.desc}</div>
                <a href={`https://${tool.url}`} target="_blank" rel="noreferrer"
                  style={{ fontSize:10,color:'#3498db' }}>{tool.url} ↗</a>
              </div>
            ))}
          </Card>

          {history.length > 0 && (
            <Card title="🕐 Recent Generations" style={{ marginTop:12 }}>
              {history.map((h,i) => (
                <div key={i} style={{ padding:'7px 0',borderBottom:'1px solid var(--border)',cursor:'pointer' }}
                  onClick={() => setResult(h.result)}>
                  <div style={{ fontSize:11,fontWeight:600,color:'var(--textPrimary)' }}>
                    {CONTENT_TYPES.find(t=>t.id===h.type)?.icon} {h.prompt}…
                  </div>
                  <div style={{ fontSize:10,color:'var(--textMuted)' }}>{new Date(h.at).toLocaleTimeString('en-KE')}</div>
                </div>
              ))}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
